// Weidner override of the blue-haven FutureResidentsClub.
//
// Diff from the parent (see mckinneyvillageapts.com/future-residents-club
// for the reference this override matches):
//   • Card ICONS ported verbatim from the sibling Weidner Vantage theme's
//     FutureResidentsClub (Repli-Multihub/vibesites-weidner-apartment-
//     homes-vantage @ 6bc4f119, src/components/FutureResidentsClub.tsx).
//     Thin-stroke (fill=none, strokeWidth=1.5) line icons that render as
//     consistent line drawings inheriting the card icon color. Kept as
//     the canonical Weidner FRC icon set — replaces an earlier
//     filled-silhouette experiment that didn't align with the sibling
//     theme's icon vocabulary. Icons stay CENTERED above the card copy.
//   • Card TITLES render in the property's --vibe-color-primary (bold,
//     centered) so they pop as branded accents rather than dark heading
//     text. Descriptions stay muted-grey and centered.
//   • The "Join Our Community" H2 is also promoted to primary color and
//     a bolder weight for the same reason.
//   • The "How It Works" section is broken out into its OWN full-bleed
//     SECONDARY-color band that sits below the benefits grid. Copy renders
//     in a luminance-picked contrast color (see `bandFg` below — mirrors
//     the runtime's auto-derived `--vibe-color-text-on-primary` but for
//     secondary, which the runtime doesn't derive) and the CTA is an
//     outlined pill in that same fg that fills on hover.
//   • Copy, content-schema keys, slot groups (how_image / cta_link) and
//     the RichTextContent "How It Works" body are IDENTICAL to the parent,
//     so editor state carries over 1:1 and property-level tweaks stay
//     compatible.
import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useGallery, useTokens } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
// The runtime auto-derives `--vibe-color-text-on-primary` from the property's
// primary color but NOT an equivalent for secondary. The How It Works band
// uses the secondary brand color as its background, so we compute the legible
// foreground at render time via the same helper Header.tsx already relies on
// (mirrors `getContrastTextColor` from `@vibesites/tokens`), then hand it to
// the CSS through a local `--frc-band-fg` custom property. `contrastRatio`
// backs the WCAG safety net below.
import {
  getContrastTextColor,
  contrastRatio,
  TEXT_ON_PRIMARY_LIGHT,
} from '../utils/luminance';

// ============================================================================
// Photo picking for the "How It Works" side image (unchanged from parent)
// ============================================================================

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  original_url?: string | null;
  source_page_urls?: string | null;
}

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'kitchen', 'bedroom', 'bathroom',
  'living room', 'closet', 'unit', 'home', 'suite',
];

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean).join(' ').toLowerCase();
}

function findInteriorPhoto(gallery: GalleryPhoto[]): GalleryPhoto | null {
  let best: GalleryPhoto | null = null;
  let bestScore = -1;
  for (const p of gallery) {
    if (!p.image) continue;
    const text = photoText(p);
    const score = INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
    if (score > bestScore) { best = p; bestScore = score; }
  }
  return best || gallery.find(p => p.image) || null;
}

const howItWorksImageDef: SlotGroupDefinition = {
  label: 'How It Works Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'VIEW FLOOR PLANS' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Icons — thin-stroke line icons ported verbatim from the sibling Weidner
// Vantage theme (Repli-Multihub/vibesites-weidner-apartment-homes-vantage,
// src/components/FutureResidentsClub.tsx @ 6bc4f119). Fetched cross-repo per
// the build-vibesite skill and copied — never imported from an external repo.
// ----------------------------------------------------------------------------
// All six icons use fill="none" + stroke="currentColor" strokeWidth=1.5 so
// they inherit the surrounding card icon color (--vibe-color-text) and render
// as consistent line drawings across the property's theme. This matches the
// Vantage line-icon vocabulary rather than the earlier filled-silhouette
// experiment — kept as the canonical Weidner icon set for FRC.
// ============================================================================

// Keep this object a self-contained literal (no `as const`, no outer
// identifier refs). The preview/schema extractor pulls every
// `const X = { … }` into a shared eval context — TS assertions and
// external names break that context and make contentSchema unextractable.
const ICON_COMMON = {
  viewBox: '0 0 48 48',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className: 'w-10 h-10',
  'aria-hidden': true,
};

function IconPreApproved() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="8" width="36" height="32" rx="2" />
      <path d="M6 16h36" />
      <path d="M16 24l4 4 8-8" />
    </svg>
  );
}

function IconFirstChoice() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="4" width="32" height="40" rx="2" />
      <path d="M16 14h16" />
      <path d="M16 22h12" />
      <path d="M16 30h8" />
      <circle cx="32" cy="36" r="3" fill="currentColor" />
    </svg>
  );
}

function IconPriority() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <path d="M24 6v36" />
      <path d="M14 12l10-6 10 6" />
      <path d="M14 20h20" />
      <path d="M18 28h12" />
      <path d="M20 36h8" />
    </svg>
  );
}

function IconCommitmentFree() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="20" r="12" />
      <path d="M18 20l4 4 8-8" />
      <path d="M16 36c0-4 4-8 8-8s8 4 8 8" />
    </svg>
  );
}

function IconGetToKnow() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="8" width="36" height="28" rx="2" />
      <path d="M6 16h36" />
      <path d="M14 24h6" />
      <path d="M14 30h10" />
      <path d="M28 24h6" />
      <path d="M28 30h6" />
      <line x1="24" y1="16" x2="24" y2="36" />
      <path d="M16 40h16" />
    </svg>
  );
}

function IconJoiningEasy() {
  return (
    <svg {...ICON_COMMON} xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="16" r="8" />
      <path d="M12 40c0-6.627 5.373-12 12-12s12 5.373 12 12" />
      <path d="M30 14l2 2-2 2" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FutureResidentsClub() {
  const title = useContentToken('futureresidentsclub.title');
  const subtitle = useContentToken('futureresidentsclub.subtitle');
  const howTitle = useContentToken('futureresidentsclub.how_title');

  const b1Title = useContentToken('futureresidentsclub.benefit1_title');
  const b1Desc = useContentToken('futureresidentsclub.benefit1_desc');
  const b2Title = useContentToken('futureresidentsclub.benefit2_title');
  const b2Desc = useContentToken('futureresidentsclub.benefit2_desc');
  const b3Title = useContentToken('futureresidentsclub.benefit3_title');
  const b3Desc = useContentToken('futureresidentsclub.benefit3_desc');
  const b4Title = useContentToken('futureresidentsclub.benefit4_title');
  const b4Desc = useContentToken('futureresidentsclub.benefit4_desc');
  const b5Title = useContentToken('futureresidentsclub.benefit5_title');
  const b5Desc = useContentToken('futureresidentsclub.benefit5_desc');
  const b6Title = useContentToken('futureresidentsclub.benefit6_title');
  const b6Desc = useContentToken('futureresidentsclub.benefit6_desc');

  const howImage = useSlotGroup('futureresidentsclub.how_image', howItWorksImageDef);
  const ctaLink = useSlotGroup('futureresidentsclub.cta_link', ctaLinkDef);

  const gallery = useGallery() as GalleryPhoto[];

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();
  const howRef = useScrollReveal<HTMLDivElement>();

  const smartPhoto = useMemo(() => findInteriorPhoto(gallery), [gallery]);
  const imageSrc = howImage.image || smartPhoto?.image;
  const imageAlt = howImage.alt || smartPhoto?.alt_text || 'Apartment interior';

  // Foreground color for the secondary-color How It Works band. Runtime picks
  // #ffffff on dark secondaries and #1a1a1a on light ones so the H3, richtext
  // body, and outlined CTA stay legible regardless of the property's brand
  // palette. Piped to the CSS via a local `--frc-band-fg` custom property.
  const themeColors = useTokens().color;
  let bandFg = getContrastTextColor(themeColors.secondary);
  // WCAG safety net — the luminance picker intentionally favors dark text on
  // some mid-tones (see the docstring on `getContrastTextColor`) which can
  // land below AA (4.5:1) contrast when the secondary color sits close to the
  // 0.55 luminance boundary. If the picked color doesn't clear AA against the
  // secondary background, flip to WHITE — the branded-strip aesthetic reads
  // better with light text than muddy dark text on ambiguous mid-tones, and
  // white always clears AA at that luminance range.
  const AA_CONTRAST = 4.5;
  if (contrastRatio(bandFg, themeColors.secondary) < AA_CONTRAST) {
    bandFg = TEXT_ON_PRIMARY_LIGHT;
  }

  return (
    <>
      {/* Top band — "Join Our Community" heading, subtitle, and the 3 × 2
          grid of benefit cards, on the standard page background. */}
      <section className="frc-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading + intro. H2 renders in --vibe-color-primary to match
              McKinney's branded section title. */}
          <div ref={headingRef} className="reveal-fade-up text-center mb-14">
            <EditableToken
              path="futureresidentsclub.title"
              defaultValue="Join Our Community"
              as="h2"
              className="frc-heading font-heading text-4xl sm:text-5xl mb-5"
            >
              {title}
            </EditableToken>
            <EditableToken
              path="futureresidentsclub.subtitle"
              defaultValue="When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:"
              as="p"
              className="font-body text-base text-[var(--vibe-color-text-muted)] max-w-3xl mx-auto leading-relaxed"
            >
              {subtitle}
            </EditableToken>
          </div>

          {/* Benefits grid — icon + title + description column, all centered
              on their card so the six benefits read as a clean 3 × 2 grid
              of branded label blocks (mirroring McKinney). */}
          <div ref={gridRef} className="reveal-fade-up frc-grid">
            {/* Pre-Approved */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconPreApproved /></div>
              <EditableToken
                path="futureresidentsclub.benefit1_title"
                defaultValue="Pre-Approved"
                as="h3"
                className="frc-card-title"
              >
                {b1Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit1_desc"
                defaultValue="Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee)."
                as="p"
                className="frc-card-desc"
              >
                {b1Desc}
              </EditableToken>
            </div>

            {/* First Choice */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconFirstChoice /></div>
              <EditableToken
                path="futureresidentsclub.benefit2_title"
                defaultValue="First Choice"
                as="h3"
                className="frc-card-title"
              >
                {b2Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit2_desc"
                defaultValue="As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market."
                as="p"
                className="frc-card-desc"
              >
                {b2Desc}
              </EditableToken>
            </div>

            {/* Priority */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconPriority /></div>
              <EditableToken
                path="futureresidentsclub.benefit3_title"
                defaultValue="Priority"
                as="h3"
                className="frc-card-title"
              >
                {b3Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit3_desc"
                defaultValue="Receive first choice and flexibility of the time-frame that you want on move-in day."
                as="p"
                className="frc-card-desc"
              >
                {b3Desc}
              </EditableToken>
            </div>

            {/* Commitment Free */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconCommitmentFree /></div>
              <EditableToken
                path="futureresidentsclub.benefit4_title"
                defaultValue="Commitment Free"
                as="h3"
                className="frc-card-title"
              >
                {b4Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit4_desc"
                defaultValue="Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities."
                as="p"
                className="frc-card-desc"
              >
                {b4Desc}
              </EditableToken>
            </div>

            {/* Get to Know Us */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconGetToKnow /></div>
              <EditableToken
                path="futureresidentsclub.benefit5_title"
                defaultValue="Get to Know Us"
                as="h3"
                className="frc-card-title"
              >
                {b5Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit5_desc"
                defaultValue="You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in."
                as="p"
                className="frc-card-desc"
              >
                {b5Desc}
              </EditableToken>
            </div>

            {/* Joining Is Easy */}
            <div className="frc-card">
              <div className="frc-card-icon"><IconJoiningEasy /></div>
              <EditableToken
                path="futureresidentsclub.benefit6_title"
                defaultValue="Joining Is Easy"
                as="h3"
                className="frc-card-title"
              >
                {b6Title}
              </EditableToken>
              <EditableToken
                path="futureresidentsclub.benefit6_desc"
                defaultValue={"Simply fill out an online application by selecting your desired floor plan, select a \"wait list\" unit, pay a holding deposit, and we'll hold your spot in line. Easy!"}
                as="p"
                className="frc-card-desc"
              >
                {b6Desc}
              </EditableToken>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — full-bleed SECONDARY-color band. Copy renders in the
          computed on-secondary contrast color (--frc-band-fg); the CTA is an
          outlined pill using the same fg. Photo sits in the right column of
          a split grid. */}
      <section
        className="frc-how-band"
        style={{ '--frc-band-fg': bandFg } as React.CSSProperties}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={howRef} className="reveal-fade-up frc-how">
            <div className="frc-how-text">
              <EditableToken
                path="futureresidentsclub.how_title"
                defaultValue="How It Works"
                as="h3"
                className="frc-how-title font-heading"
              >
                {howTitle}
              </EditableToken>
              <RichTextContent
                path="futureresidentsclub.how_body"
                label="How It Works"
                defaultValue="<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>"
                as="div"
                className="frc-how-body font-body text-sm leading-relaxed [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-2 [&_strong]:font-semibold [&_a]:underline"
              />
              <EditableSlotGroup groupPath="futureresidentsclub.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/floor-plans'}
                  target={ctaLink._target || '_self'}
                  rel={ctaLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="frc-cta"
                >
                  {ctaLink.label || 'VIEW FLOOR PLANS'}
                </a>
              </EditableSlotGroup>
            </div>

            <EditableSlotGroup groupPath="futureresidentsclub.how_image" definition={howItWorksImageDef}>
              <div className="frc-how-photo">
                {imageSrc ? (
                  <VibesitesImage
                    url={imageSrc}
                    alt={imageAlt}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
                )}
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      <style>{`
        /* -------------------------------------------------------------- */
        /* Top band — "Join Our Community" + benefits grid                */
        /* -------------------------------------------------------------- */
        .frc-top {
          padding: 4rem 0;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .frc-top { padding: 5rem 0 6rem; }
        }

        /* Section H2 in the property's brand color so the "Join Our
           Community" title reads as a branded accent, matching the McKinney
           reference (which renders the same heading in its brand pink). */
        .frc-heading {
          font-weight: 700;
          color: var(--vibe-color-primary);
          letter-spacing: -0.01em;
          line-height: 1.15;
        }

        .frc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 3rem 2.5rem;
        }
        @media (max-width: 768px) {
          .frc-grid { grid-template-columns: 1fr; gap: 2.5rem; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .frc-grid { grid-template-columns: repeat(2, 1fr); }
        }

        /* Cards centered so the icon → title → description column reads as
           a stacked block (parent-theme layout, restored). */
        .frc-card {
          text-align: center;
          padding: 0.5rem 0.5rem;
        }
        .frc-card-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 1.1rem;
          color: var(--vibe-color-text);
        }
        /* Card TITLE in the brand primary color — bold and centered so the
           six benefits read as branded label rows above their descriptions,
           mirroring McKinney's title treatment. */
        .frc-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.3rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0 0 0.7rem;
          line-height: 1.2;
          letter-spacing: 0;
        }
        .frc-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted);
          line-height: 1.65;
          margin: 0 auto;
          max-width: 22rem;
        }

        /* -------------------------------------------------------------- */
        /* How It Works — full-bleed SECONDARY-color band                 */
        /* -------------------------------------------------------------- */
        /* Fill = property's --vibe-color-secondary; foreground = the
           luminance-picked contrast color set as --frc-band-fg on the
           section's inline style. This mirrors what --vibe-color-text-on-
           primary does for the primary color (runtime doesn't derive an
           equivalent for secondary), keeping the copy legible whether the
           property's secondary is a dark or light brand color. */
        .frc-how-band {
          padding: 4rem 0;
          background: var(--vibe-color-secondary);
          color: var(--frc-band-fg);
        }
        @media (min-width: 768px) {
          .frc-how-band { padding: 5rem 0; }
        }

        .frc-how {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
        }
        @media (max-width: 768px) {
          .frc-how { grid-template-columns: 1fr; gap: 2rem; }
        }

        .frc-how-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--frc-band-fg);
          margin: 0 0 1.25rem;
          line-height: 1.15;
        }
        @media (min-width: 768px) {
          .frc-how-title { font-size: 2.25rem; }
        }
        .frc-how-body {
          color: var(--frc-band-fg);
        }

        .frc-how-photo {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .frc-how-photo img {
          transition: transform 0.5s ease;
        }
        .frc-how-photo:hover img {
          transform: scale(1.03);
        }

        /* Outlined CTA — border + label take the band's fg color so the
           button always contrasts with the secondary fill. On hover the
           button fills with the fg color and the label flips to the
           SECONDARY brand color (matching the primary-band pattern where
           the label flips to primary on hover). */
        .frc-cta {
          display: inline-block;
          margin-top: 1.5rem;
          padding: 0.85rem 2.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--frc-band-fg);
          background: transparent;
          border: 1.5px solid var(--frc-band-fg);
          border-radius: 9999px;
          transition: background 0.25s ease, color 0.25s ease, transform 0.2s ease;
        }
        .frc-cta:hover {
          background: var(--frc-band-fg);
          color: var(--vibe-color-secondary);
          transform: translateY(-1px);
        }
        .frc-cta:active {
          transform: translateY(0);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Schema — matches the parent 1:1 so per-property editor state stays valid
// against this override.
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FutureResidentsClub',
  displayName: 'Future Residents Club',
  tokens: {
    'futureresidentsclub.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Join Our Community',
    },
    'futureresidentsclub.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: "When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:",
    },
    'futureresidentsclub.how_title': {
      label: 'How It Works Title',
      type: 'text',
      defaultValue: 'How It Works',
    },
    'futureresidentsclub.how_body': {
      label: 'How It Works Body',
      type: 'richtext',
      defaultValue: "<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>",
    },
    'futureresidentsclub.benefit1_title': { label: 'Pre-Approved Title', type: 'text', defaultValue: 'Pre-Approved' },
    'futureresidentsclub.benefit1_desc': { label: 'Pre-Approved Description', type: 'text', defaultValue: 'Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee).' },
    'futureresidentsclub.benefit2_title': { label: 'First Choice Title', type: 'text', defaultValue: 'First Choice' },
    'futureresidentsclub.benefit2_desc': { label: 'First Choice Description', type: 'text', defaultValue: 'As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market.' },
    'futureresidentsclub.benefit3_title': { label: 'Priority Title', type: 'text', defaultValue: 'Priority' },
    'futureresidentsclub.benefit3_desc': { label: 'Priority Description', type: 'text', defaultValue: 'Receive first choice and flexibility of the time-frame that you want on move-in day.' },
    'futureresidentsclub.benefit4_title': { label: 'Commitment Free Title', type: 'text', defaultValue: 'Commitment Free' },
    'futureresidentsclub.benefit4_desc': { label: 'Commitment Free Description', type: 'text', defaultValue: "Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities." },
    'futureresidentsclub.benefit5_title': { label: 'Get to Know Us Title', type: 'text', defaultValue: 'Get to Know Us' },
    'futureresidentsclub.benefit5_desc': { label: 'Get to Know Us Description', type: 'text', defaultValue: 'You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in.' },
    'futureresidentsclub.benefit6_title': { label: 'Joining Is Easy Title', type: 'text', defaultValue: 'Joining Is Easy' },
    'futureresidentsclub.benefit6_desc': { label: 'Joining Is Easy Description', type: 'text', defaultValue: 'Simply fill out an online application by selecting your desired floor plan, select a "wait list" unit, pay a holding deposit, and we\'ll hold your spot in line. Easy!' },
  },
  slotGroups: {
    'futureresidentsclub.how_image': howItWorksImageDef,
    'futureresidentsclub.cta_link': ctaLinkDef,
  },
};

(FutureResidentsClub as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FutureResidentsClub;
